import { useAuth } from 'react-oidc-context';
import { TestingAuthInfo } from '../modules/TestingAuthInfo';
import { E2E_TEST_SUBJECT, TESTING_AUTH_INFO_STORAGE_KEY } from '../automatedTesting/constants';
import { SignoutRedirectArgs, User } from 'oidc-client-ts';
import { useMemo } from 'react';

export interface CustomAuth {
  error?: Error;
  isLoading?: boolean;
  isAuthenticated: boolean;
  user?: User;
  signoutRedirect?: (args?: SignoutRedirectArgs) => Promise<void>;
}

export function getCustomAuth(): CustomAuth | undefined {
  const objectFromStorage = sessionStorage.getItem(TESTING_AUTH_INFO_STORAGE_KEY);
  const loginInfo = objectFromStorage ? (JSON.parse(objectFromStorage) as TestingAuthInfo) : undefined;

  if (!loginInfo) {
    return undefined;
  }

  // Return auth info from storage
  return {
    isAuthenticated: true,
    user: new User({
      access_token: loginInfo.access_token,
      token_type: 'Bearer',
      profile: {
        name: loginInfo.userGlobalId,
        sub: E2E_TEST_SUBJECT,
        iss: '',
        aud: '',
        exp: 1,
        iat: 1,
        given_name: 'e2e',
        family_name: 'tests',
        email: loginInfo.email,
      },
    }),
  };
}

// A wrapper over the useAuth from react-oidc-context, to support end-to-end testing
export const useCustomAuth = (): CustomAuth => {
  const oAuthInfo = useAuth();
  const customAuth = useMemo(() => getCustomAuth(), []);

  if (customAuth) {
    return customAuth;
  }

  if (!oAuthInfo.isAuthenticated) {
    return { isAuthenticated: false, isLoading: oAuthInfo.isLoading };
  }

  if (!oAuthInfo.user) {
    throw new Error('User expected');
  }

  return {
    error: oAuthInfo.error,
    isLoading: oAuthInfo.isLoading,
    isAuthenticated: oAuthInfo.isAuthenticated,
    user: oAuthInfo.user,
    signoutRedirect: oAuthInfo.signoutRedirect,
  };
};
